import { cn, InfoPopover, Pill, PillProps, Stack } from 'component-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactLabelNameEnum, SpeciesEnum } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { Card } from '@/components/widgets/o/Card/Card';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { resolveThemeColor } from '@/utils/resolveThemeColor';

type CardBaseProps = {
  title: string;
  tooltip?: string;
  children: ReactNode;
  value?: string;
};

const CardBase = ({ title, tooltip, children, value }: CardBaseProps) => {
  return (
    <Card
      styles={{
        root: 'min-w-[100px] grow lg:basis-1/3 rounded-3xl',
        body: 'flex flex-col gap-4 p-8',
      }}
    >
      <Stack direction='row' className='w-full justify-between'>
        <Stack spacing={2} direction='row' center centerMain>
          <span className='typography-body1 text-text-primary'>{title}</span>
          {tooltip && <InfoPopover title={title} body={tooltip} />}
        </Stack>
        {typeof value === 'string' && <span className='typography-body1 text-text-primary'>{value}</span>}
      </Stack>
      {children}
    </Card>
  );
};

const getMarinePillStyle = (value: number) => {
  if (value >= 80) {
    return { primary: resolveThemeColor('success-light'), text: resolveThemeColor('success-dark') };
  }
  if (value >= 60) {
    return { primary: resolveThemeColor('warning-light'), text: resolveThemeColor('warning-dark') };
  }

  return { primary: resolveThemeColor('orange-light'), text: resolveThemeColor('orange-dark') };
};

export const MarinePill = ({
  value,
  unit,
  displayOptions,
  className,
  ...delegated
}: { value: number; unit?: UnitEnum; displayOptions?: Intl.NumberFormatOptions } & PillProps) => {
  const { t } = useTranslation();
  const style = getMarinePillStyle(value);
  const displayUnit = unit ? t(`global.ui.unit.${unit}`) : '';
  const displayValue = `${getDisplayNumber(value, window.navigator.language, displayOptions)} ${displayUnit}`;

  return (
    <Pill
      className={cn('typography-overline font-bold w-fit rounded-2xl py-2 px-4', className)}
      style={{
        color: style?.text,
        backgroundColor: style?.primary,
      }}
      {...delegated}
    >
      <span className='typography-body1 text-text-primary'>{displayValue}</span>
    </Pill>
  );
};

export const SurvivalRateCard = ({ species }: { species: SpeciesEnum }) => {
  const { t } = useTranslation();
  const survivalFacts = usePlotFacts<number>([IndicatorName.marine_survival_rate_1y]);
  const survivalFact = getFactFromFactArray(survivalFacts, IndicatorName.marine_survival_rate_1y, {
    [PlotReportFactLabelNameEnum.species]: species,
  });

  if (typeof survivalFact?.value !== 'number') {
    return null;
  }

  const displaySpecies = species.substring(0, 1).toUpperCase() + species.substring(1);

  return (
    <CardBase
      title={t('shared.ncaDetail.details.speciesGradient.labels.survivalRate')}
      tooltip={t('shared.ncaDetail.details.speciesGradient.explainer.survivalRate', {
        species: displaySpecies,
      })}
    >
      <MarinePill value={survivalFact.value} unit={survivalFact.unit} />
    </CardBase>
  );
};

export const BiodiversityUpliftCard = ({ species }: { species: SpeciesEnum }) => {
  const { t } = useTranslation();
  const biodiversityUpliftFacts = usePlotFacts<number>([IndicatorName.marine_biodiversity_uplift]);
  const biodiversityUpliftFact = getFactFromFactArray(
    biodiversityUpliftFacts,
    IndicatorName.marine_biodiversity_uplift,
    {
      [PlotReportFactLabelNameEnum.species]: species,
    },
  );

  if (typeof biodiversityUpliftFact?.value !== 'number') {
    return null;
  }

  const displaySpecies = species.substring(0, 1).toUpperCase() + species.substring(1);

  return (
    <CardBase
      title={t('shared.ncaDetail.details.speciesGradient.labels.biodiversityUplift')}
      tooltip={t('shared.ncaDetail.details.speciesGradient.explainer.biodiversityUplift', {
        species: displaySpecies,
      })}
    >
      <MarinePill value={biodiversityUpliftFact.value} unit={biodiversityUpliftFact.unit} />
    </CardBase>
  );
};

export const ProjectProgressCard = ({ species }: { species: SpeciesEnum }) => {
  const { t } = useTranslation();

  const projectProgressFacts = usePlotFacts<number>([IndicatorName.marine_project_progress]);
  const projectProgressFact = getFactFromFactArray(projectProgressFacts, IndicatorName.marine_project_progress, {
    [PlotReportFactLabelNameEnum.species]: species,
  });

  if (typeof projectProgressFact?.value !== 'number') {
    return null;
  }

  const unit = t(`global.ui.unit.${projectProgressFact.unit}`);

  return (
    <CardBase
      title={t('shared.ncaDetail.details.speciesGradient.labels.projectProgress')}
      tooltip={t('shared.ncaDetail.details.speciesGradient.explainer.projectProgress')}
      value={`${getDisplayNumber(projectProgressFact.value)} ${unit}`}
    >
      <ProjectProgressBar value={projectProgressFact.value} />
    </CardBase>
  );
};

const ProjectProgressBar = ({ value }: { value: number }) => {
  const min = 0;
  const max = 100;
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className='h-8 w-full rounded-full border border-divider'>
      <div
        className='h-full rounded-full'
        style={{ width: `${percentage}%`, background: 'linear-gradient(to right, #73dbe1, #1d4e9e)' }}
      />
    </div>
  );
};
